import React from 'react';
import { BubbleMenu, Editor } from '@tiptap/react';
import { 
  Bold, Italic, Code, Link2, Strikethrough, 
  Highlighter, Heading1, Heading2, Heading3 
} from 'lucide-react';

interface BubbleMenuBarProps {
  editor: Editor;
}

export function BubbleMenuBar({ editor }: BubbleMenuBarProps) {
  if (!editor || !editor.isEditable) {
    return null;
  }

  const addLink = () => {
    const url = window.prompt('Enter URL');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <BubbleMenu 
      editor={editor} 
      tippyOptions={{ 
        duration: 100,
        placement: 'top',
        onHidden: () => {
          // Clean up any lingering tippy elements
          const tippyElements = document.querySelectorAll('[data-tippy-root]');
          tippyElements.forEach(el => {
            if (el.parentNode) {
              try {
                el.parentNode.removeChild(el);
              } catch (error) {
                // Ignore removal errors
              }
            }
          });
        }
      }}
      shouldShow={({ editor, state }) => {
        const { selection } = state;
        const { empty } = selection;

        // Don't show menu if editor is not editable
        if (!editor.isEditable) {
          return false;
        }

        // Don't show menu for empty selection
        if (empty) {
          return false;
        }

        return true;
      }}
      className="flex items-center space-x-1 bg-white rounded-lg shadow-lg border p-2"
    >
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={`p-1.5 rounded hover:bg-gray-100 ${
          editor.isActive('heading', { level: 1 }) ? 'bg-gray-200' : ''
        }`}
      >
        <Heading1 className="w-4 h-4" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={`p-1.5 rounded hover:bg-gray-100 ${
          editor.isActive('heading', { level: 2 }) ? 'bg-gray-200' : ''
        }`}
      >
        <Heading2 className="w-4 h-4" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={`p-1.5 rounded hover:bg-gray-100 ${
          editor.isActive('heading', { level: 3 }) ? 'bg-gray-200' : ''
        }`}
      >
        <Heading3 className="w-4 h-4" />
      </button>
      <div className="w-px h-5 bg-gray-200" />
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`p-1.5 rounded hover:bg-gray-100 ${
          editor.isActive('bold') ? 'bg-gray-200' : ''
        }`}
      >
        <Bold className="w-4 h-4" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`p-1.5 rounded hover:bg-gray-100 ${
          editor.isActive('italic') ? 'bg-gray-200' : ''
        }`}
      >
        <Italic className="w-4 h-4" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={`p-1.5 rounded hover:bg-gray-100 ${
          editor.isActive('strike') ? 'bg-gray-200' : ''
        }`}
      >
        <Strikethrough className="w-4 h-4" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHighlight().run()}
        className={`p-1.5 rounded hover:bg-gray-100 ${
          editor.isActive('highlight') ? 'bg-gray-200' : ''
        }`}
      >
        <Highlighter className="w-4 h-4" />
      </button>
      <div className="w-px h-5 bg-gray-200" />
      <button
        onClick={() => editor.chain().focus().toggleCode().run()}
        className={`p-1.5 rounded hover:bg-gray-100 ${
          editor.isActive('code') ? 'bg-gray-200' : ''
        }`}
      >
        <Code className="w-4 h-4" />
      </button>
      <button
        onClick={addLink}
        className={`p-1.5 rounded hover:bg-gray-100 ${
          editor.isActive('link') ? 'bg-gray-200' : ''
        }`}
      >
        <Link2 className="w-4 h-4" />
      </button>
    </BubbleMenu>
  );
}